import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, UserListItem, UserListResponse, UserVm } from '@/api/types'

export interface UserRoleLookup {
  id: number
  name: string
}

export interface UserEmployeeLookup {
  id: number
  fullName: string
  email?: string
}

class UserService {
  async getAllUsers(branchId: number, pageNumber: number, pageSize: number): Promise<UserListResponse> {
    const url = endpoints.user.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<UserListResponse> | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      const data = 'data' in payload && Array.isArray(payload.data) ? payload.data : payload.data?.data || payload.data || []
      const totalRecords = payload.totalRecords ?? data.length ?? 0
      const pageNo = payload.pageNumber ?? pageNumber
      const size = payload.pageSize ?? pageSize

      return {
        totalRecords,
        pageNumber: pageNo,
        pageSize: size,
        data: (data as any[]).map(normalizeUserListItem),
      }
    }

    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getUserById(branchId: number, id: number): Promise<UserVm | null> {
    const url = endpoints.user.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<UserVm> | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      const data = (payload as any).data ?? payload
      return data as UserVm
    }
    return null
  }

  async getRoles(branchId: number): Promise<UserRoleLookup[]> {
    const url = endpoints.user.getAllRoles(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map((r) => ({
      id: r.id ?? r.Id ?? 0,
      name: r.name ?? r.Name ?? r.roleName ?? r.RoleName ?? '',
    }))
  }

  async getEmployees(branchId: number): Promise<UserEmployeeLookup[]> {
    const url = endpoints.user.getAllEmployees(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] =
      Array.isArray(payload) ? payload :
      Array.isArray(payload?.data) ? payload.data :
      []
    return list.map((e) => {
      const firstName = e.empFirstNameEn ?? e.EmpFirstNameEn ?? e.firstName ?? e.FirstName ?? ''
      const lastName = e.empLastNameEn ?? e.EmpLastNameEn ?? e.lastName ?? e.LastName ?? ''
      const fullName = (e.fullName ?? e.FullName ?? `${firstName} ${lastName}`).trim()
      return {
        id: e.id ?? e.Id ?? e.empId ?? e.EmpId ?? 0,
        fullName,
        email: e.email ?? e.Email ?? e.empEmail ?? e.EmpEmail,
      }
    })
  }

  async createUser(vm: UserVm): Promise<any> {
    const url = endpoints.user.create
    const response = await apiClient.post(url, vm)
    return response.data
  }

  async updateUser(vm: UserVm): Promise<any> {
    const url = endpoints.user.update
    const response = await apiClient.put(url, vm)
    return response.data
  }
}

function normalizeUserListItem(item: any): UserListItem {
  return {
    id: item.id ?? item.Id ?? 0,
    empId: item.empId ?? item.EmpId ?? 0,
    roleId: item.roleId ?? item.RoleId ?? 0,
    branchId: item.branchId ?? item.BranchId ?? 0,
    fullName: item.fullName ?? item.FullName,
    roleName: item.roleName ?? item.RoleName ?? item.role ?? item.Role,
    status: item.status ?? item.Status,
    isActive: item.isActive ?? item.IsActive,
    branchName: item.branchName ?? item.BranchName,
    createdDate: item.createdDate ?? item.CreatedDate ?? null,
  }
}

export const userService = new UserService()

